import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Circle,
  MapTrifold,
  Question,
  Trophy,
} from '@phosphor-icons/react';

import { cn } from '@/lib/utils';

/* ── Types locaux (miroir des types serveur, packages/application/src/trpc/rows/hg-college.ts) ── */

export type HgCategorieReponseQcm = 'notion_mobilisee' | 'confusion_identifiee' | 'absence_de_repere';
export type HgNiveauMaitrise = 1 | 2 | 3 | 4;

export interface HgQcmChoixRow {
  id: string;
  libelle: string;
}

export interface HgQcmItemRow {
  id: string;
  question: string;
  support: string | null;
  support_implicite: boolean;
  critere_id: string;
  choix: HgQcmChoixRow[];
}

export interface HgQcmExerciceRow {
  id: string;
  fiche_id: string;
  fiche_code: string;
  fiche_titre: string;
  position: number;
  enonce: string;
  consigne: string | null;
  duree_indicative_s: number | null;
  items: HgQcmItemRow[];
}

export interface HgQcmRetourItemRow {
  item_id: string;
  notion_code: string | null;
  notion: string | null;
  categorie: HgCategorieReponseQcm;
  je_ne_sais_pas: boolean;
  explication: string | null;
}

export interface HgQcmRetourCritereRow {
  critere_id: string;
  code: string;
  libelle: string;
  niveau_atteint: HgNiveauMaitrise;
  categorie_dominante: HgCategorieReponseQcm;
  aide_utilisee: boolean;
  n_observations: number;
  dominance_etablie: boolean;
  items: HgQcmRetourItemRow[];
}

export interface HgQcmTentativeResultRow {
  tentative_id: string;
  tentative_n: number;
  niveau_global: HgNiveauMaitrise;
  aide_utilisee: boolean;
  criteres: HgQcmRetourCritereRow[];
}

/* Descripteurs de l'unique critère QCM (index.md §Q), portés côté client pour
   l'affichage — la classification/le niveau restent calculés côté serveur. */
export const NIVEAU_DESCRIPTIONS: Record<HgNiveauMaitrise, string> = {
  1: 'Absence de repère répétée sur la notion.',
  2: 'Notion identifiée, mais confusions récurrentes avec un ou plusieurs distracteurs.',
  3: 'Notion mobilisée sans confusion, sur un support explicite.',
  4: "Notion mobilisée sans confusion, y compris sur un support implicite ou non familier.",
};

/* Valeur de choix reservee a l'ignorance assumee. Elle ne quitte jamais l'ecran :
   la route recoit `choixId: null` et `jeNeSaisPas: true`, deux champs distincts. */
export const CHOIX_JE_NE_SAIS_PAS = '__je_ne_sais_pas__';

/* Les descripteurs de §Q disent « recurrentes » et « repetee » — deux mots qui
   supposent PLUSIEURS erreurs de meme nature. Quand une seule erreur a fixe le
   niveau, la phrase serait fausse : on dit alors ce qui s'est reellement passe.
   Le niveau, lui, n'est pas retouche — il vient de la table §G. */
const DESCRIPTIONS_ERREUR_UNIQUE: Partial<Record<HgCategorieReponseQcm, string>> = {
  confusion_identifiee: 'Une confusion avec un distracteur, sur une notion par ailleurs mobilisée.',
  absence_de_repere: 'Une réponse hors du champ de la question, sur une notion par ailleurs mobilisée.',
};

export function descriptionNiveau(critere: {
  niveau_atteint: HgNiveauMaitrise;
  categorie_dominante: HgCategorieReponseQcm;
  dominance_etablie: boolean;
}): string {
  if (!critere.dominance_etablie) {
    const specifique = DESCRIPTIONS_ERREUR_UNIQUE[critere.categorie_dominante];
    if (specifique) return specifique;
  }
  return NIVEAU_DESCRIPTIONS[critere.niveau_atteint];
}

export const CATEGORIE_LABELS: Record<HgCategorieReponseQcm, string> = {
  notion_mobilisee: 'Notion mobilisée',
  confusion_identifiee: 'Confusion identifiée',
  absence_de_repere: 'Absence de repère',
};

/* ── Vue d'un exercice — sans API ni état propre ──────────────────────────────── */

export interface HgQcmExerciceVueProps {
  exercice: HgQcmExerciceRow;
  index: number;
  total: number;
  /* Une entree par serie de la fiche, dans l'ordre : vrai si l'eleve l'a deja
     validee. Sert la progression, jamais un score — on compte des series
     terminees, pas des reponses justes. */
  seriesFaites?: readonly boolean[];
  /* Nombre de questions de la fiche entiere, series confondues. La vue ne voit
     que la serie courante et ne peut pas le deduire. */
  totalQuestions?: number;
  answers: Record<string, string | null>;
  result?: HgQcmTentativeResultRow;
  submitting?: boolean;
  submitError?: string | null;
  onAnswer: (itemId: string, choixId: string) => void;
  onPrecedent: () => void;
  onSuivant: () => void;
  onValider: () => void;
}

/* « 2 séries terminées » compte des SÉRIES VALIDÉES, jamais des réponses justes :
   c'est un repère de parcours, pas une note déguisée (Décision 1, option B). */
export function libelleProgression(seriesFaites: readonly boolean[]): string {
  const faites = seriesFaites.filter(Boolean).length;
  if (faites === 0) return `${seriesFaites.length} séries à parcourir`;
  if (faites === seriesFaites.length) return 'Fiche terminée';
  return faites === 1 ? '1 série terminée' : `${faites} séries terminées`;
}

export function HgQcmExerciceVue({
  exercice,
  index,
  total,
  seriesFaites,
  totalQuestions,
  answers,
  result,
  submitting = false,
  submitError = null,
  onAnswer,
  onPrecedent,
  onSuivant,
  onValider,
}: HgQcmExerciceVueProps) {
  const allAnswered = exercice.items.length > 0 && exercice.items.every((item) => answers[item.id]);

  const annonceRetour = result
    ? `Réponse enregistrée. ${result.criteres
        .map((critere) => `${critere.libelle} : niveau ${critere.niveau_atteint} sur 4`)
        .join('. ')}.`
    : '';

  return (
    <div>
      <p role="status" className="sr-only">{annonceRetour}</p>
      <div className="flex items-start gap-[10px] mb-2">
        <MapTrifold size={24} weight="light" className="text-primary shrink-0 mt-[2px]" />
        <h2 className="text-[19px] font-bold text-foreground leading-[1.3]">{exercice.fiche_titre}</h2>
      </div>
      {seriesFaites && seriesFaites.length > 1 && (
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <div className="flex gap-[5px]" aria-hidden="true">
            {seriesFaites.map((faite, rang) => (
              <span
                key={rang}
                className={cn(
                  'h-[6px] w-9 rounded-full',
                  faite ? 'bg-naia-blue' : rang === index ? 'bg-naia-sky' : 'bg-border',
                )}
              />
            ))}
          </div>
          <span className="text-[14px] text-muted-foreground">
            {libelleProgression(seriesFaites)}
          </span>
        </div>
      )}
      <div className="text-[14px] text-muted-foreground mb-6">
        Série {index + 1} / {total} — {exercice.items.length} question{exercice.items.length > 1 ? 's' : ''}
      </div>

      {exercice.items.length === 0 ? (
        <p className="text-muted-foreground text-sm mb-6">Contenu indisponible pour cette question.</p>
      ) : result ? (
        <RetourExercice result={result} items={exercice.items} />
      ) : (
        <QuestionExercice items={exercice.items} answers={answers} onAnswer={onAnswer} />
      )}

      {submitError && <p role="alert" className="text-destructive text-sm mt-4">{submitError}</p>}

      <div className="flex gap-3 mt-7">
        <button
          type="button"
          disabled={index === 0}
          onClick={onPrecedent}
          className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card border border-border bg-card text-foreground font-bold text-[15px] cursor-pointer min-h-[44px] transition-shadow hover:shadow-card disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
        >
          <ArrowLeft size={18} />
          Précédent
        </button>

        {!result && exercice.items.length > 0 && (
          <button
            type="button"
            disabled={!allAnswered || submitting}
            onClick={onValider}
            className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card bg-primary text-primary-foreground font-bold text-[15px] border-none cursor-pointer min-h-[44px] transition-shadow hover:shadow-card disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none"
          >
            <CheckCircle size={18} weight="bold" />
            {submitting ? 'Envoi…' : 'Valider'}
          </button>
        )}

        {result && index < total - 1 && (
          <button
            type="button"
            onClick={onSuivant}
            className="inline-flex items-center gap-2 px-5 py-[11px] rounded-card bg-primary text-primary-foreground font-bold text-[15px] border-none cursor-pointer min-h-[44px] transition-shadow hover:shadow-card"
          >
            <ArrowRight size={18} />
            Suivant
          </button>
        )}
      </div>

      {result && index === total - 1 && (
        /* La fin de fiche est le seul moment où l'écran félicite. Le badge de
           niveau apparaît à chaque série et dit « où tu en es » ; cette carte
           dit « tu es allé au bout », ce qui n'est pas la même chose et ne se
           mérite qu'une fois.

           Aucun emoji (décision 2), et aucun compte de bonnes réponses
           (décision 1) : le nombre de séries et de questions est un
           DÉNOMINATEUR — ce qui a été parcouru — jamais un score. */
        <section className="mt-7 rounded-card border border-naia-orange-bg bg-card p-5">
          <p className="flex items-center gap-[10px] m-0 text-[22px] font-extrabold text-foreground mb-2">
            <Trophy size={26} weight="fill" className="text-naia-orange shrink-0" />
            Bien joué.
          </p>
          <p className="m-0 text-[15px] text-muted-foreground leading-[1.6]">
            {totalQuestions
              ? `Tu es allé au bout de cette fiche : ${total} séries, ${totalQuestions} questions.`
              : 'Tu es allé au bout de cette fiche.'}{' '}
            Le détail de chaque question reste au-dessus, si tu veux y revenir.
          </p>
        </section>
      )}
    </div>
  );
}

/* ── Sous-composants ─────────────────────────────────────────────────────────── */

export function QuestionExercice({
  items,
  answers,
  onAnswer,
}: {
  items: HgQcmItemRow[];
  answers: Record<string, string | null>;
  onAnswer: (itemId: string, choixId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-7">
      {items.map((item) => (
        <div key={item.id}>
          {item.support && (
            <div className="bg-card border border-border rounded-card p-4 text-[15px] leading-[1.65] text-foreground mb-4 whitespace-pre-wrap">
              {item.support}
            </div>
          )}
          <fieldset className="border-0 p-0 m-0 min-w-0">
            <legend className="text-[18px] font-bold text-foreground leading-[1.4] mb-4 p-0">{item.question}</legend>
            <div className="flex flex-col gap-[10px]">
              {item.choix.map((choix) => (
                <label
                  key={choix.id}
                  className={cn(
                    'flex items-center gap-3 px-4 py-[13px] rounded-card border border-border bg-card text-foreground font-[600] text-[15px] leading-[1.45] cursor-pointer text-left min-h-[44px]',
                    'transition-shadow transition-colors hover:shadow-card hover:border-primary',
                    'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-naia-blue',
                    answers[item.id] === choix.id && 'border-primary bg-primary text-primary-foreground shadow-card'
                  )}
                >
                  <input
                    type="radio"
                    name={`hg-qcm-${item.id}`}
                    value={choix.id}
                    checked={answers[item.id] === choix.id}
                    onChange={() => onAnswer(item.id, choix.id)}
                    className="sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className="inline-flex shrink-0 size-[18px] rounded-full border-2 border-current items-center justify-center"
                  >
                    {answers[item.id] === choix.id && (
                      <span className="size-[9px] rounded-full bg-current" />
                    )}
                  </span>
                  <span className="min-w-0">{choix.libelle}</span>
                </label>
              ))}
              <label
                className={cn(
                  'flex items-center gap-3 px-4 py-[13px] rounded-card border border-dashed border-border bg-transparent text-muted-foreground font-[600] text-[15px] cursor-pointer text-left min-h-[44px] mt-1',
                  'transition-colors hover:border-primary hover:text-foreground',
                  'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-naia-blue',
                  answers[item.id] === CHOIX_JE_NE_SAIS_PAS && 'border-solid border-primary text-foreground bg-accent',
                )}
              >
                <input
                  type="radio"
                  name={`hg-qcm-${item.id}`}
                  value={CHOIX_JE_NE_SAIS_PAS}
                  checked={answers[item.id] === CHOIX_JE_NE_SAIS_PAS}
                  onChange={() => onAnswer(item.id, CHOIX_JE_NE_SAIS_PAS)}
                  className="sr-only"
                />
                <span
                  aria-hidden="true"
                  className="inline-flex shrink-0 size-[18px] rounded-full border-2 border-current items-center justify-center"
                >
                  {answers[item.id] === CHOIX_JE_NE_SAIS_PAS && (
                    <span className="size-[9px] rounded-full bg-current" />
                  )}
                </span>
                <Question size={17} weight="bold" className="shrink-0" />
                <span className="min-w-0">Je ne sais pas</span>
              </label>
            </div>
          </fieldset>
        </div>
      ))}
    </div>
  );
}

export function RetourExercice({ result, items }: { result: HgQcmTentativeResultRow; items: HgQcmItemRow[] }) {
  const questionByItemId = new Map(items.map((item) => [item.id, item.question]));
  return (
    <div className="flex flex-col gap-4">
      {result.criteres.map((critere) => (
        <div key={critere.critere_id} className="bg-card border border-border rounded-card p-5">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <h2 className="text-[15px] font-bold text-foreground m-0">{critere.libelle}</h2>
            <NiveauBadge niveau={critere.niveau_atteint} />
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {descriptionNiveau(critere)}
          </p>

          <div className="flex flex-col gap-3">
            {critere.items.map((itemRetour) => (
              <div key={itemRetour.item_id} className="border-t border-border pt-3">
                {itemRetour.notion && (
                  <h3 className="text-[14.5px] font-bold text-foreground mb-[6px] mt-0">{itemRetour.notion}</h3>
                )}
                <div className="text-[14px] text-muted-foreground mb-1">
                  {questionByItemId.get(itemRetour.item_id) ?? ''}
                </div>
                <div className="text-[14px] text-muted-foreground mb-1">
                  {itemRetour.je_ne_sais_pas
                    ? 'Tu as répondu « je ne sais pas »'
                    : CATEGORIE_LABELS[itemRetour.categorie]}
                </div>
                {itemRetour.explication && (
                  <p className="text-[15px] text-muted-foreground leading-[1.55]">{itemRetour.explication}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function NiveauBadge({ niveau }: { niveau: HgNiveauMaitrise }) {
  if (niveau >= 3) {
    return (
      <span className="inline-flex items-center gap-[6px] px-3 py-[5px] rounded-full bg-naia-orange-bg text-naia-orange font-extrabold text-[12.5px] tracking-[0.02em]">
        <CheckCircle size={15} weight="fill" />
        Niveau {niveau}/4
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-[6px] px-3 py-[5px] rounded-full bg-accent text-primary font-extrabold text-[12.5px] tracking-[0.02em]">
      <Circle size={15} weight="bold" />
      Niveau {niveau}/4
    </span>
  );
}
